import { useCallback } from "react"
import type { Dashboard, OrganizationDetail, OrganizationSummary, OrganizationUser } from "@/schema/organization-portal"
import { useQuery } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"
import { useDevAuth } from "@/hooks/use-dev-auth"

import { organizationFetch } from "./api"

export function useOrganizations() {
  const context = useApiContext()
  return useQuery({
    queryKey: ["organizations"],
    queryFn: async () => apiContextFetch<{ organizations: OrganizationSummary[] }>(await context(), "organizations"),
  })
}
export function useOrganization(id: string) {
  const context = useApiContext()
  return useQuery({
    queryKey: ["organization", id],
    queryFn: async () => apiContextFetch<OrganizationDetail>(await context(), `organizations/${id}`),
    enabled: !!id,
  })
}
export function useOrganizationQuery<T>(organizationId: string, route: string, refresh?: number) {
  const context = useApiContext()
  const bookId = useBookId()
  const { userId } = useDevAuth()
  return useQuery({
    queryKey: ["organization_data", organizationId || "", bookId, route, userId],
    queryFn: async () => organizationFetch<T>(await context(), organizationId, route),
    enabled: !!organizationId || !!bookId,
    refetchInterval: refresh,
  })
}
export function useDashboard(organizationId: string, id: string) {
  const context = useApiContext()
  const bookId = useBookId()
  const { userId } = useDevAuth()
  return useQuery({
    queryKey: ["organization_data", organizationId || "", bookId, `dashboards/${id}`, userId],
    queryFn: async () => organizationFetch<Dashboard>(await context(), organizationId, `dashboards/${id}`),
    enabled: !!id,
    refetchInterval: (query) =>
      [...(query.state.data?.widgets ?? []), ...(query.state.data?.published?.widgets ?? [])].some(
        (widget) => widget.refreshing
      )
        ? 2_000
        : false,
  })
}

export function useOrganizationUsers(organizationId: string, search: string, enabled = true) {
  const context = useApiContext()
  const select = useCallback(
    (data: { users: OrganizationUser[] }) =>
      data.users.filter((user) =>
        `${user.name} ${user.email} ${user.company_name}`.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  )
  return useQuery({
    queryKey: ["organization_users", organizationId],
    queryFn: async () =>
      apiContextFetch<{ users: OrganizationUser[] }>(await context(), `organizations/${organizationId}/users`),
    enabled: enabled && !!organizationId,
    select,
  })
}
